import type { MegaBlockDef, LockedDoorDef, BlockadeWallDef, BarricadeDef } from '../systems/BoardSystem';

/** Rich per-seed detail for the PerfectSolver run (mirrors `SolverSeedDetail` in scripts/audit-levels.ts). */
export interface SolverSeedDetail {
  seed: number;
  /** 'win' | 'loss' | 'timeout' | 'budget' from SearchResult.kind. */
  kind: 'win' | 'loss' | 'timeout' | 'budget';
  /** Witness length (only present for wins). */
  witnessLen?: number;
  /** SearchStats.uniqueStates. */
  uniqueStates: number;
  /** SearchStats.maxDepth. */
  maxDepth: number;
  /** SearchStats.elapsedMs. */
  elapsedMs: number;
}

/**
 * PerfectSolver per-queue-count ground-truth winnability result
 * (mirrors `SolverResult` in scripts/audit-levels.ts).
 *
 * Keyed under `QueueResult.solver` so it doesn't collide with the
 * simulator-derived `rate`. "unknown" is the union of seeds that hit
 * the timeout or state-budget cap — winnability was not decided for them.
 */
export interface SolverResult {
  /** winCount / decidedCount (excludes unknown from denominator). */
  rate: number;
  wins: number[];
  losses: number[];
  unknown: number[];
  /** Per-seed detailed outcome + stats for the perfect-solver run. */
  seedDetails?: SolverSeedDetail[];
}

/** Per-queue-count audit result: win rate + winning seed offsets + difficulty label. */
export interface QueueResult {
  rate: number;
  seeds: number[];
  difficulty?: string;
  /** PerfectSolver ground-truth winnability for this (level, numQueues). Present only when `--bot perfect` or `--bot all` was run. */
  solver?: SolverResult;
}

/**
 * Returns the cached win rate for a level at its configured numQueues.
 * Falls back to 0 if the cache or queue entry is missing.
 */
export function getEffectiveP(level: Pick<ImageLevel, 'cache' | 'numQueues'>): number {
  const cache = level.cache;
  if (!cache) return 0;
  const numQueues = level.numQueues ?? 3;
  const qKey = `q${numQueues}` as keyof AuditCache;
  const qResult = cache[qKey] as QueueResult | undefined;
  return qResult?.rate ?? 0;
}

/**
 * The seed every level used before per-level seeds existed. Any level without
 * an explicit `seed` field keeps generating exactly the queue it always has.
 */
export const DEFAULT_LEVEL_SEED = 42;

/**
 * The authoritative queue-generation seed for a level.
 *
 * This is THE accessor for "which queue does the player get" — the runtime
 * (`GameScene`), the audit worker and the solver (`initialState`) all resolve
 * the seed through it, so a solver proof and the shipped game are always
 * talking about the same queue.
 *
 * Note this is deliberately NOT {@link getWinningSeed}: that one reads
 * `cache.qN.seeds`, which is the *simulator bot's* list of winning seed
 * offsets from an audit run. Perfect-bot audits write `seeds: []` (see
 * `audit-worker.ts`, `runSimulator === false` ⇒ `winSeeds` stays empty), so
 * it is empty for all 1,583 cached levels in `data/` and is only a debug
 * readout. Baking a chosen seed is what `ImageLevel.seed` is for.
 */
export function getLevelSeed(level: Pick<ImageLevel, 'seed'> | undefined): number {
  return level?.seed ?? DEFAULT_LEVEL_SEED;
}

/**
 * Returns the first winning seed for a level at its configured numQueues,
 * or undefined if none is available.
 *
 * Debug readout only (InfoOverlay) — see {@link getLevelSeed} for the seed the
 * game actually generates its queue from.
 */
export function getWinningSeed(level: Pick<ImageLevel, 'cache' | 'numQueues'>): number | undefined {
  const cache = level.cache;
  if (!cache) return undefined;
  const numQueues = level.numQueues ?? 3;
  const qKey = `q${numQueues}` as keyof AuditCache;
  const qResult = cache[qKey] as QueueResult | undefined;
  return qResult?.seeds?.[0];
}

// ── `gen` proof block ────────────────────────────────────────────────────────
//
// Everything the level factory bakes into a level file when it proves that
// level shippable. Unlike `cache` (an audit *summary*, keyed by numQueues and
// written by `audit-levels.ts`), `gen` is a *claim about this exact file*: the
// config + seed it was proven at, the solver revision that proved it, and the
// archived witness that reproduces the proof in milliseconds.
//
// Written only by `src/levels/genWriter.ts`, read by the build extractors and
// by `scripts/verify-corpus.ts`.

/**
 * The generator knobs the difficulty driver settled on for this level.
 *
 * These duplicate fields that also live in `numQueues`/`difficulty`; the copy
 * here is what the proof was actually taken at, so a later hand-edit of
 * `difficulty` is detectable rather than silently invalidating the proof.
 */
export interface GenConfig {
  numQueues: number;
  multiSlotChance: number;
  multiColorChance: number;
  ammoSurplusRatio: number;
  multiSlotColorSplitChance: number;
}

/**
 * How the WIN was obtained.
 * - `search` — a bookless PerfectSolver search found it.
 * - `book-assisted` — an opening-book `forcedPrefix` was needed (only ever
 *   valid at the seed the book entry was recorded for).
 */
export type GenProofMethod = 'search' | 'book-assisted';

/**
 * A winnability proof for the shipped `(level, seed, config)` triple.
 *
 * Only WIN is representable: a level that cannot be proven does not get a
 * `gen` block, it gets hidden or quarantined.
 */
export interface GenProof {
  verdict: 'WIN';
  /** Content hash of the solver-relevant sources — see `PerfectSolver/solverRev.ts`. */
  solverRev: string;
  /** State budget the proving run was given (search parameter, not a measurement). */
  maxStates: number;
  /** SearchStats.uniqueStates for the proving run. */
  uniqueStates: number;
  /** SearchStats.maxDepth for the proving run. */
  maxDepth: number;
  /** Number of actions in the winning witness. */
  witnessLen: number;
  method: GenProofMethod;
  /** Repo-relative path of the gzipped witness — see `PerfectSolver/witnessArchive.ts`. */
  witnessRef: string;
  /** ISO-8601 timestamp of the proving run. */
  provedAt: string;
}

/** Difficulty bucket label. Advisory only — shipping is gated on provability, not hardness. */
export type GenBucket = 'tutorial' | 'easy' | 'normal' | 'hard';

export interface GenScore {
  /** `uniqueStates / max(witnessLen, 1)` — how much the solver had to back up. */
  backtrackRatio: number;
  bucket: GenBucket;
}

/** Criterion-5 lane report: same-color runs within a single queue lane, at the baked seed. */
export interface GenC5 {
  /** Longest same-color per-orb run in any one lane. Ship gate is <= 4. */
  maxRunPerLane: number;
  /** Number of runs of exactly length 3. */
  runs3: number;
  /** Number of runs of length >= 4. */
  runs4plus: number;
  /** Count of adjacent multi-orb attacker pairs within a lane. */
  multiOrbAdjacent: number;
}

/**
 * Art-quality metrics, measured on gameplay-quantized pixels (not `visualGrid`).
 * Optional for now — art gating lands in Phase 1/2 of the roadmap.
 */
export interface GenArt {
  /** Distinct gameplay colors in play. */
  colors: number;
  /** Share of non-empty cells held by the single most common color. */
  domShare: number;
  /** Cell count of the least-used gameplay color. */
  minColorCells: number;
  /** Mean CIE76 deltaE, source slice -> visualGrid. */
  meanDeltaE: number;
  /** p95 CIE76 deltaE, source slice -> visualGrid. */
  p95DeltaE: number;
  /** Minimum pairwise LAB distance within `gameplayPalette`. */
  legibilityMinPair: number;
  /** Count of small high-contrast source features that lost distinctness. */
  salientLoss: number;
  /** Count of hue buckets that collided onto one gameplay color. */
  bucketCollisions: number;
  /** Hash of the frozen art fields — grid, gameplayMap, gameplayPalette, visualGrid, visualPalette. */
  gridHash: string;
  /** True once a human has approved this level's contact sheet. */
  approved: boolean;
}

/**
 * The proof block a level must carry to ship. Written as one compact JSON
 * line by {@link "./genWriter"!writeGenBlock}, alongside the top-level `seed:`
 * it was proven at (the two are always written together and must agree).
 */
export interface GenBlock {
  config: GenConfig;
  /** The queue-generation seed this proof is about — mirrors `ImageLevel.seed`. */
  seed: number;
  proof: GenProof;
  score: GenScore;
  c5: GenC5;
  art?: GenArt;
}

/** Full audit cache data, embedded directly in the level file. */
export interface AuditCache {
  gridHash: string;
  name?: string;
  cellCount: number;
  colorCount: number;
  attackerCount: number;
  timestamp: string;
  /** Lookahead depth used for this audit run. Missing = legacy depth 2. */
  depth?: number;
  q3?: QueueResult;
  q4?: QueueResult;
  q5?: QueueResult;
  q6?: QueueResult;
}

export interface ImageLevel {
  name: string;
  grid: number[][]; // any size (padded to 42x42 at runtime), -1 = empty, 0+ = color index
  /** Column indices that contain snake blocks (vertical chains, hit from top only, clear bottom-up). */
  snakeCols?: number[];
  /** Mega blocks placed on the board (coordinates in source grid space, remapped during padding). */
  megaBlocks?: MegaBlockDef[];
  /** Locked doors that cover sections of the board, hiding pixels until enough cubes of the door's color are cleared. */
  lockedDoors?: LockedDoorDef[];
  /** Frozen attacker indices: which queue attackers (by index) should be frozen, and with what defrost counter. */
  frozenIndices?: { index: number; counter: number }[];
  /** Blockade walls on board edges that block shots until destroyed by matching-color attackers. */
  blockadeWalls?: BlockadeWallDef[];
  /** Barricades that cover board cells, broken only by hammers (not by clearing cubes). */
  barricades?: BarricadeDef[];
  /** Queue positions where hammer items should be inserted (hammers auto-trigger to break barricades). */
  hammerIndices?: number[];
  /** If true, the level is hidden from players (used for filtering out Easy/NEVER_WINNABLE levels without deleting them). */
  hidden?: boolean;
  /** Number of queue lanes for this level (default 3, max 6). More lanes = more parallel dispatch options. */
  numQueues?: number;
  /** Number of stack/waiting slots for this level (default MAX_STACK_SLOTS = 5). Lower values = harder, higher = easier. */
  stackSlots?: number;
  /** Permanent UUID v4 identifier for stable deep-linking. Baked into each level file, never changes. */
  uuid?: string;
  /**
   * Queue-generation seed baked into the level. Every consumer of the level
   * (runtime `GameScene`, `audit-worker`, PerfectSolver `initialState`) resolves
   * it through {@link getLevelSeed}, so a proof found for this seed is a proof
   * about the queue the player actually receives.
   *
   * Omitted ⇒ {@link DEFAULT_LEVEL_SEED} (42), which is what every level used
   * before this field existed — so leaving it out is byte-identical to the old
   * hardcoded behaviour.
   */
  seed?: number;
  /** Baked difficulty config — computed at generation/backfill time so the level's attacker composition is deterministic regardless of position in the level list. */
  difficulty?: {
    multiSlotChance: number;
    multiColorChance: number;
    mysteryChance: number;
    frozenChance: number;
    minAmmo: number;
    maxAmmo: number;
    ammoSurplusRatio: number;
    /**
     * Optional override for `DifficultyConfig.multiSlotColorSplitChance` —
     * the probability that an attacker which is already multi-slot gets its
     * orbs split across colors. Omitted ⇒ 0.9 (the historical hardcoded rate).
     */
    multiSlotColorSplitChance?: number;
  };
  /**
   * Factory proof block — the config, seed, winnability proof, difficulty
   * score, criterion-5 lane report and art metrics this level was shipped on.
   *
   * Present ⇒ the level was proven WIN at `gen.seed` (which always equals
   * {@link ImageLevel.seed}) under `gen.proof.solverRev`. Absent ⇒ the level
   * predates the factory and is only backed by `cache`.
   *
   * Written exclusively by `src/levels/genWriter.ts`.
   */
  gen?: GenBlock;
  /** Offline audit cache data — embedded directly in the level file. Contains per-queue-count winnability results. */
  cache?: AuditCache;
  /** Visual hex color palette for rich pixel-art levels. Each entry is a CSS hex string (e.g. "#e87c2f"). */
  visualPalette?: string[];
  /** Visual grid: each cell is an index into visualPalette (-1 = empty). Same dimensions as grid. */
  visualGrid?: number[][];
  /** Maps each visualPalette index to a GAME_COLORS index for gameplay matching. */
  gameplayMap?: number[];
  /** Per-level gameplay color palette — actual hex colors for each compact gameplay index. When present, attackers/cubes render in these colors instead of GAME_COLORS. */
  gameplayPalette?: string[];
}

export type { MegaBlockDef, LockedDoorDef, BlockadeWallDef, BarricadeDef };

// Color map: .=empty, R=red(0), B=blue(1), G=green(2), Y=yellow(3), P=purple(4), O=orange(5)
//            C=cyan(6), K=pink(7), W=white(8), T=teal(9), N=brown(10), M=cream(11)
export function p(rows: string[]): number[][] {
  const m: Record<string, number> = {
    '.': -1, R: 0, B: 1, G: 2, Y: 3, P: 4, O: 5,
    C: 6, K: 7, W: 8, T: 9, N: 10, M: 11,
  };

  if (import.meta.env.DEV) {
    const validChars = new Set(Object.keys(m));
    for (let i = 0; i < rows.length; i++) {
      for (let j = 0; j < rows[i].length; j++) {
        const c = rows[i][j];
        if (!validChars.has(c)) {
          console.warn(`[p()] Invalid character '${c}' at row ${i}, col ${j} — treated as empty`);
        }
      }
    }
    const lengths = rows.map(r => r.length);
    const maxLen = Math.max(...lengths);
    const minLen = Math.min(...lengths);
    if (maxLen !== minLen) {
      console.warn(`[p()] Inconsistent row widths: min=${minLen}, max=${maxLen} — short rows will be padded`);
    }
  }

  // Normalize all rows to the same width (max row length), padding short rows with empty cells
  const maxLen = Math.max(...rows.map(r => r.length));
  return rows.map(row => {
    const padded = row.padEnd(maxLen, '.');
    return [...padded].map(c => m[c] ?? -1);
  });
}
